import { useEffect, useRef } from 'react';

interface ModalProps {
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Extra classes for the inner panel (e.g. "max-w-2xl p-6"). Defaults to "max-w-2xl".
   * Ignored when `raw` is true.
   */
  panelClassName?: string;
  /**
   * When true the Modal renders only the backdrop overlay and lets the first
   * child element act as the panel (e.g. when the child is already a <Card>).
   * Click-outside detection still works via the backdrop onMouseDown check.
   */
  raw?: boolean;
  /** When true, backdrop clicks do NOT close (use for forms with unsaved state). */
  disableBackdropClose?: boolean;
}

/**
 * Shared modal wrapper.
 * - Closes on backdrop click (unless disableBackdropClose is set)
 * - Closes on Escape key
 * - Traps body scroll while open
 */
export default function Modal({ onClose, children, panelClassName, raw, disableBackdropClose }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableBackdropClose) return;
    // Only close if the click landed directly on the backdrop (not on the panel)
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (raw) {
    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
        onMouseDown={handleBackdropClick}
      >
        <div ref={panelRef} className="w-full flex items-start justify-center my-auto">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto"
      onMouseDown={handleBackdropClick}
    >
      <div
        ref={panelRef}
        className={`bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full my-auto ${panelClassName ?? 'max-w-2xl'}`}
      >
        {children}
      </div>
    </div>
  );
}
